import $ from 'jquery';
import {FocusTrap} from 'focus-trap';
import fscreen from 'fscreen';
import FocusTrapQueue from '../FocusTrapQueue';
import ModalBodyManager from './ModalBodyManager';
import {getWorkingTouchListForEvent, testIsFakeMozMouseEvent,} from '../../../utils/dom-events';
import SvgIcon from '../../../utils/SvgIcon';
import IconSprites from '../../../utils/IconSprites';
import IModalConfig from './IModalConfig';
import IModal from './IModal';
import createAriaLiveElement from '../../../utils/screen-reader/createAriaLiveElement';
import { MODAL_LIVE_REGION_CLASS } from './constants';

let modalIdCounter = 0;

function focus(el: Element) {
  if (el === document.activeElement) {
    return;
  }

  if ((el as HTMLElement).focus) {
    (el as HTMLElement).focus();
  } else {
    try {
      // IE11 SVGElement does not have a focus method
      window.HTMLElement.prototype.focus.call(el);
    } catch (e) {
    }
  }

  if (el.tagName.toLowerCase() === 'input') {
    (el as HTMLInputElement).select();
  }
}

const defaults: IModalConfig = {
  title: 'Default Title',
  bodyContents: 'Default Content',
  className: '',
  footerContents: undefined,
  closeOnEscape: true,
  closeOnBackdrop: true,
  closeButtonInHeader: true,
  returnFocusTo: undefined,
  beforeOpen: () => { },
  beforeClose: () => { },
  beforeRemove: () => { },
  headingId: '',
  describedById: undefined,
  ariaRole: 'dialog',
};

const EVENT_NAMESPACE = 'tnModal';

const Events = {
  RESIZE: 'resize',
  KEYDOWN: 'keydown',
  TRANSITION_IN: `transitionend.${EVENT_NAMESPACE}.in`,
  TRANSITION_OUT: `transitionend.${EVENT_NAMESPACE}.out`,
  DISMISS_ITEM_CLICK: `click.${EVENT_NAMESPACE}`,
  DISMISS_ITEM_PRESS: `touchstart.${EVENT_NAMESPACE} mousedown.${EVENT_NAMESPACE}`,
  DISMISS_ITEM_TOUCHEND: `touchend.${EVENT_NAMESPACE}`,
  DISMISS_ITEM_MOUSEUP: `mouseup.${EVENT_NAMESPACE}`,
};

class Modal implements IModal {
  static defaults = defaults;

  $el: JQuery<HTMLElement>;

  $backdropEl: JQuery<HTMLElement>;

  $dismissLayer: JQuery<any>;

  $dismissItems: JQuery<any>;

  isActiveModal = false;

  isTransitionedIn = false;

  isClosed = false;

  focusTrap: FocusTrap | undefined;

  lastFocusedElement: Element | null | undefined;

  constructor(public options: IModalConfig) {
    if (!this.options.headingId) {
      this.options.headingId = `tn-modal-title-${++modalIdCounter}`;
    }

    if (!this.options.returnFocusTo) {
      this.options.returnFocusTo = document.activeElement;
    }

    this.$el = $(this.makeDialogEl());
    this.$backdropEl = this.makeBackdropEl();
    this.$dismissLayer = this.find('.modal-dismiss-layer');
    this.$dismissItems = this.find('[data-dismiss=modal]');

    if (this.options.closeOnBackdrop) {
      this.$dismissItems = this.$dismissItems.add(this.$dismissLayer);
    }
  }

  setActiveModal() {
    if (this.isActiveModal) {
      return;
    }

    this.enableEscapeKeyEvent();
    this.unpauseFocusTrap();

    this.isActiveModal = true;
  }

  unsetActiveModal() {
    if (!this.isActiveModal) {
      return;
    }

    this.disableEscapeKeyEvent();
    this.pauseFocusTrap();

    this.isActiveModal = false;
  }

  open() {
    this.$el.css('display', 'block');

    this.attachTo(fscreen.fullscreenElement || document.body);

    this.createFocusTrap();

    this.initWindowResizeEvent();

    window.requestAnimationFrame(() => {
      this.setupTransitionInEvent();

      window.requestAnimationFrame(() => {
        this.$el.addClass('in');
        this.$backdropEl.addClass('in');
        this.adjustDialog();

        if (this.options.beforeOpen) {
          this.options.beforeOpen();
        }
      });
    });
  }

  attachTo(element: HTMLElement) {
    if (element !== this.$el[0].parentElement) {
      element.insertAdjacentElement('beforeend', this.$el[0]);
      element.insertAdjacentElement('beforeend', this.$backdropEl[0]);
    }
  }

  setupTransitionInEvent() {
    // Try to see if there's a CSS transition setup for .modal-dialog's `transform`
    // property. If so, delay calling `afterTransitionedIn` until the transition is
    // complete. If not, call it immediately.

    const afterTransitionedIn = () => {
      this.isTransitionedIn = true;

      this.initDismissItemClick();

      this.enableEscapeKeyEvent();
      this.unpauseFocusTrap();
    };

    const $dialog = this.$el.find('.modal-dialog');

    if ($dialog.length) {
      const { transitionProperty, transform } = getComputedStyle($dialog[0]);

      const isTransformTransitioned = transform !== 'none'
        && (transitionProperty.includes('all') || transitionProperty.includes('transform'));

      if (isTransformTransitioned) {
        const onTransitionEnd = (event: JQuery.TriggeredEvent) => {
          if ((event.originalEvent as TransitionEvent).propertyName.includes('transform')) {
            afterTransitionedIn();
            this.$el.off(Events.TRANSITION_IN, onTransitionEnd);
          }
        };
        this.$el.on(Events.TRANSITION_IN, onTransitionEnd);

        return;
      }
    }

    afterTransitionedIn();
  }

  initDismissItemClick() {
    let listeningFor: string;
    let pressTarget: Element;

    this.$dismissItems.on(Events.DISMISS_ITEM_CLICK, (event) => {
      if (testIsFakeMozMouseEvent(event.originalEvent)) {
        return;
      }
      this.dismiss();
    });

    this.$dismissItems.on(Events.DISMISS_ITEM_PRESS, (event) => {
      if (testIsFakeMozMouseEvent(event.originalEvent)) {
        return;
      }
      event.preventDefault();

      pressTarget = event.target;
      listeningFor = event.type === 'mousedown' ? 'mouseup' : 'touchend';
    });

    const releaseFilter = (event: JQuery.TriggeredEvent, handler: () => void) => {
      if (
        event.type === listeningFor
        && testIsFakeMozMouseEvent(event.originalEvent) === false
      ) {
        handler();
      }
    };

    this.$el.on(Events.DISMISS_ITEM_TOUCHEND, (event) => {
      releaseFilter(event, () => {
        const touch = getWorkingTouchListForEvent(event.originalEvent)[0];
        const el = document.elementFromPoint(touch.clientX, touch.clientY);

        if (el === pressTarget) {
          event.preventDefault();
          this.dismiss();
        }
      });
    });

    this.$el.on(Events.DISMISS_ITEM_MOUSEUP, (event) => {
      releaseFilter(event, () => {
        if (event.target === pressTarget) {
          this.dismiss();
        }
      });
    });
  }

  dismiss() {
    this.close();
  }

  initWindowResizeEvent() {
    $(window).on(Events.RESIZE, this.adjustDialog);
  }

  createFocusTrap() {
    this.focusTrap = FocusTrapQueue.createFocusTrap(this.$el[0], {
      escapeDeactivates: false,
      fallbackFocus: this.$el[0],
      returnFocusOnDeactivate: false,
    });

    this.focusTrap.activate();
  }

  unpauseFocusTrap() {
    const { lastFocusedElement } = this;

    this.focusTrap?.unpause();

    if (lastFocusedElement) {
      focus(lastFocusedElement);
    }
  }

  pauseFocusTrap() {
    this.lastFocusedElement = document.activeElement;

    this.focusTrap?.pause();
  }

  deactivateFocusTrap() {
    this.focusTrap?.deactivate();
  }

  returnFocus() {
    const $el = this.options.returnFocusTo instanceof Function
      ? this.options.returnFocusTo()
      : this.options.returnFocusTo;

    focus($el);
  }

  updateBaseZIndex(value: number) {
    this.$backdropEl.css('zIndex', value);
    this.$el.css('zIndex', value + 1);
  }

  getBaseZIndex() {
    const value = this.$el.css('zIndex');

    return parseInt(value, 10);
  }

  enableEscapeKeyEvent() {
    if (!this.isTransitionedIn) {
      return;
    }

    if (this.options.closeOnEscape) {
      $(document.body).on(Events.KEYDOWN, this.onEscapeKey);
    }
  }

  disableEscapeKeyEvent() {
    if (this.options.closeOnEscape) {
      $(document.body).off(Events.KEYDOWN, this.onEscapeKey);
    }
  }

  onEscapeKey = (event: JQuery.TriggeredEvent) => {
    if (event.which === 27) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.dismiss();
    }
  };

  adjustDialog = () => {
    const isBodyOverflowing = ModalBodyManager.getIsBodyOverflowing();
    const widthPx = `${ModalBodyManager.getScrollbarWidth()}px`;
    const el = this.$el[0];
    const dismissLayer = this.$dismissLayer[0];
    const isModalOverflowing = el.scrollHeight > document.documentElement.clientHeight;

    if (!isBodyOverflowing && isModalOverflowing) {
      el.style.paddingLeft = widthPx;
    }

    if (isBodyOverflowing && !isModalOverflowing) {
      el.style.paddingRight = widthPx;
    }

    if (isModalOverflowing) {
      dismissLayer.style.right = widthPx;
    } else {
      dismissLayer.style.right = 0;
    }
  };

  find(selector: string) {
    return this.$el.find(selector);
  }

  removeCloseHandlers() {
    const nsSelector = `.${EVENT_NAMESPACE}`;

    $(window).off(Events.RESIZE, this.adjustDialog);
    this.disableEscapeKeyEvent();

    this.$el.off(nsSelector);
    this.$dismissItems.off(nsSelector);
  }

  close() {
    if (this.isClosed) {
      return;
    }

    this.isClosed = true;

    this.removeCloseHandlers();

    this.$el.removeClass('in');
    this.$backdropEl.removeClass('in');
    this.$el.one(Events.TRANSITION_OUT, () => {
      if (this.options.beforeRemove) {
        this.options.beforeRemove();
      }

      this.$el.remove();
      this.$backdropEl.remove();

      this.$dismissLayer = $();
      this.$dismissItems = $();
      this.$el = $();
      this.$backdropEl = $();
      this.lastFocusedElement = undefined;
      this.options = {};
    });

    if (this.options.beforeClose) {
      this.options.beforeClose();
    }
  }

  makeDialogEl(): JQuery {
    const {
      title, closeButtonInHeader, className, headingId, describedById, ariaRole,
    } = this.options;

    const closeButtonHtml = closeButtonInHeader
      ? `<button type="button" class="close tn-focus-outline" data-dismiss="modal" aria-label="Close">
            ${SvgIcon.getHtml(IconSprites.X, 'tn-modal-header__icon-close')}
        </button>`
      : '';
    
    const dialogAriaDescribedBy = describedById
      ? `aria-describedby="${describedById}"`
      : '';
    
    const bodyId = describedById
      ? `id="${describedById}"`
      : '';
    
    let $el = $(`
        <div class="modal fade ${className}" role="${ariaRole}" aria-modal="true" aria-labelledby="${headingId}" ${dialogAriaDescribedBy}>
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        ${closeButtonHtml}
                        <h1 class="modal-title" id="${headingId}">${title}</h1>
                    </div>
                    <div class="modal-body" ${bodyId}></div>
                </div>
            </div>
            <div class="modal-dismiss-layer"></div>
        </div>`
    );

    $el.find('.modal-body').append(this.options.bodyContents);

    if (this.options.footerContents) {
        const $footer = $(`<div class="modal-footer"></div>`);
        $footer.append(this.options.footerContents);
        $el.find('.modal-content').append($footer);
    }

    $el.append(createAriaLiveElement([MODAL_LIVE_REGION_CLASS]));

    return $el;
  }

  makeBackdropEl() {
    return $('<div class="modal-backdrop fade"></div>');
  }

  scrollToTop() {
    this.$el.animate({ scrollTop: 0 }, 'fast');
  }
}

export default Modal;
